import { Avatar, Box, Card, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { mediaSourceUrl } from './utils'
import { IconProjectPlaceholder } from '../../../icons/IconProjectPlaceholder'
import { createNotificationNavHandlers, getActorPathKey, getProjectSlug } from './notificationNav'
import { ActorNameSpan, ProjectNameQuoted } from './notificationTypography'
import { notificationBodyWrapSx } from './notificationTextClamp'

type Props = {
  notification: Extract<KnownNotificationResource, { type: 'user_followed_author_project_recruitment_updated' }>
}

export function FollowedAuthorProjectRecruitmentUpdatedNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { goToProject, onProjectClick, onUserClick } = createNotificationNavHandlers(navigate)
  const payload = notification.payload
  const project = payload.project
  const author = payload.author
  const eventMeta = payload.event_meta

  const projectName = typeof project?.name === 'string' && project.name.trim().length > 0 ? project.name : 'проект'
  const authorName =
    typeof author?.fullname === 'string' && author.fullname.trim().length > 0 ? author.fullname : 'Автор'
  const projectAvatarSrc = mediaSourceUrl(project?.image) ?? undefined
  const authorAvatarSrc = mediaSourceUrl(author?.avatar) ?? undefined
  const projectSlug = getProjectSlug(project)
  const authorPathKey = getActorPathKey(author, notification.actor_user_id)

  const rolesCount = Array.isArray(eventMeta?.roles) ? eventMeta?.roles.length ?? 0 : 0
  const needsCount = Array.isArray(eventMeta?.needs) ? eventMeta?.needs.length ?? 0 : 0

  return (
    <Card
      variant="primaryAdaptiveCard"
      sx={{ p: 2, cursor: projectSlug ? 'pointer' : 'default' }}
      onClick={() => goToProject(projectSlug)}
    >
      <Stack direction="column" minWidth={0} flex={1} gap={1.25}>
        <Box
          onClick={(e) => onProjectClick(e, projectSlug)}
          sx={{ cursor: projectSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={projectName}
            avatarSrc={projectAvatarSrc}
            fallbackIcon={<IconProjectPlaceholder fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle="Обновлен набор в проект"
          />
        </Box>

        <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
          <Box
            onClick={authorPathKey ? (e) => onUserClick(e, authorPathKey) : undefined}
            sx={{ cursor: authorPathKey ? 'pointer' : 'default', flexShrink: 0 }}
          >
            <Avatar src={authorAvatarSrc} alt={authorName} sx={{ width: 34, height: 34 }} />
          </Box>
          <Stack minWidth={0}>
            <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
              <ActorNameSpan pathKey={authorPathKey} onUserClick={onUserClick}>
                {authorName}
              </ActorNameSpan>
              {' обновил(а) набор в проекте '}
              <ProjectNameQuoted name={projectName} pathKey={projectSlug} onNameClick={onProjectClick} />
            </Typography>
            {(rolesCount > 0 || needsCount > 0) && (
              <Typography variant="black-12-light" sx={{ opacity: 0.85, ...notificationBodyWrapSx }}>
                Роли: {rolesCount} • Потребности: {needsCount}
              </Typography>
            )}
          </Stack>
        </Stack>

        <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
          {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Card>
  )
}
